import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TouchEvent } from "react";
import DOMPurify from "dompurify";
import { Header } from "../components/Header";
import { ProductGrid } from "../components/ProductGrid";
import { api, type Product, type StoreSettings } from "../lib/api";

type Route =
  | { name: "home" }
  | { name: "product"; slug: string }
  | { name: "admin" };

function getRoute(): Route {
  const path = window.location.pathname;
  if (path === "/admin") return { name: "admin" };
  if (path.startsWith("/products/")) return { name: "product", slug: decodeURIComponent(path.replace("/products/", "")) };
  return { name: "home" };
}

function navigate(path: string) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function App() {
  const [route, setRoute] = useState<Route>(getRoute);
  const [store, setStore] = useState<StoreSettings | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [isAdmin, setAdmin] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);
  const [isBusy, setBusy] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setRefreshing] = useState(false);
  const touchStartY = useRef<number | null>(null);

  const refreshStorefront = useCallback(async (showInitialLoader = false) => {
    if (showInitialLoader) setBusy(true);
    try {
      const [storeData, productData] = await Promise.all([api.getStore(), api.getProducts()]);
      setStore(storeData);
      setProducts(productData);
      setLoadError("");
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "Не удалось загрузить витрину");
    } finally {
      if (showInitialLoader) setBusy(false);
    }
  }, []);

  useEffect(() => {
    const onPopState = () => setRoute(getRoute());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    refreshStorefront(true);
  }, [refreshStorefront]);

  useEffect(() => {
    let lastRefresh = 0;

    function refreshOnResume() {
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      if (now - lastRefresh < 3000) return;
      lastRefresh = now;
      refreshStorefront();
    }

    window.addEventListener("focus", refreshOnResume);
    window.addEventListener("pageshow", refreshOnResume);
    document.addEventListener("visibilitychange", refreshOnResume);

    return () => {
      window.removeEventListener("focus", refreshOnResume);
      window.removeEventListener("pageshow", refreshOnResume);
      document.removeEventListener("visibilitychange", refreshOnResume);
    };
  }, [refreshStorefront]);

  useEffect(() => {
    if (route.name !== "admin") return;
    setAdminChecked(false);
    api
      .getAdminMe()
      .then(() => setAdmin(true))
      .catch(() => setAdmin(false))
      .finally(() => setAdminChecked(true));
  }, [route]);

  const visibleProducts = useMemo(
    () => products.filter((product) => product.visibility === "published" && product.availabilityStatus !== "hidden"),
    [products]
  );

  function handleTouchStart(event: TouchEvent<HTMLElement>) {
    if (window.scrollY > 0) return;
    touchStartY.current = event.touches[0]?.clientY ?? null;
  }

  function handleTouchMove(event: TouchEvent<HTMLElement>) {
    if (touchStartY.current === null || window.scrollY > 0) return;
    const currentY = event.touches[0]?.clientY ?? 0;
    const distance = Math.max(0, currentY - touchStartY.current);
    if (distance > 8) setPullDistance(Math.min(96, Math.round(distance * 0.55)));
  }

  async function handleTouchEnd() {
    touchStartY.current = null;
    if (pullDistance < 64 || isRefreshing) {
      setPullDistance(0);
      return;
    }

    setRefreshing(true);
    setPullDistance(72);
    await refreshStorefront();
    setRefreshing(false);
    setPullDistance(0);
  }

  if (isBusy) return <main className="app-shell loading">Загружаем витрину...</main>;

  if (loadError || !store) {
    return (
      <main className="app-shell loading">
        <div className="locked-panel">
          <h3>Витрина не подключена</h3>
          <p>{loadError || "Не удалось загрузить настройки магазина."}</p>
        </div>
      </main>
    );
  }

  return (
    <main
      className="app-shell"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={() => {
        touchStartY.current = null;
        setPullDistance(0);
      }}
    >
      <div
        className={`pull-refresh ${pullDistance > 0 ? "visible" : ""}`}
        style={{ transform: `translate(-50%, ${pullDistance - 72}px)` }}
        aria-hidden="true"
      >
        {isRefreshing ? "Обновляем..." : "Потяните для обновления"}
      </div>
      {route.name !== "product" ? <Header store={store} /> : null}
      {route.name === "home" ? (
        <ProductGrid products={visibleProducts} onOpen={(slug) => navigate(`/products/${slug}`)} />
      ) : null}
      {route.name === "product" ? (
        <ProductFeed products={visibleProducts} initialSlug={route.slug} store={store} onBack={() => navigate("/")} />
      ) : null}
      {route.name === "admin" ? (
        <AdminView
          products={products}
          onBack={() => navigate("/")}
          onProductsChange={setProducts}
          isAdmin={isAdmin}
          adminChecked={adminChecked}
        />
      ) : null}
    </main>
  );
}

function ProductFeed({
  products,
  initialSlug,
  store,
  onBack
}: {
  products: Product[];
  initialSlug: string;
  store: StoreSettings;
  onBack: () => void;
}) {
  const itemRefs = useRef(new Map<string, HTMLElement>());
  const didScrollToInitial = useRef(false);

  useEffect(() => {
    didScrollToInitial.current = false;
  }, [initialSlug]);

  useEffect(() => {
    if (didScrollToInitial.current || products.length === 0) return;
    const target = itemRefs.current.get(initialSlug);
    if (!target) return;
    didScrollToInitial.current = true;
    requestAnimationFrame(() => target.scrollIntoView({ block: "start" }));
  }, [initialSlug, products]);

  useEffect(() => {
    if (products.length === 0) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        const slug = visible?.target.getAttribute("data-slug");
        const currentRoute = getRoute();
        if (!slug || currentRoute.name !== "product" || currentRoute.slug === slug) return;
        window.history.replaceState({}, "", `/products/${encodeURIComponent(slug)}`);
      },
      { threshold: [0.45, 0.7] }
    );

    itemRefs.current.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [products]);

  return (
    <section className="product-feed" aria-label="Лента товаров">
      <div className="feed-topbar">
        <button className="text-button" onClick={onBack}>
          Назад
        </button>
      </div>
      {products.length > 0 ? (
        products.map((product) => (
          <ProductFeedItem
            key={product.id}
            product={product}
            store={store}
            refCallback={(element) => {
              if (element) itemRefs.current.set(product.slug, element);
              else itemRefs.current.delete(product.slug);
            }}
          />
        ))
      ) : (
        <div className="locked-panel">Пока нет опубликованных товаров.</div>
      )}
    </section>
  );
}

function ProductFeedItem({
  product,
  store,
  refCallback
}: {
  product: Product;
  store: StoreSettings;
  refCallback: (element: HTMLElement | null) => void;
}) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const lightboxTouchStart = useRef<{ x: number; y: number } | null>(null);
  const fallbackSeller = extractSellerFromDescription(product.description);
  const sellerContactUrl = product.sellerContactUrl ?? fallbackSeller?.url;
  const sellerContactLabel = product.sellerContactLabel ?? fallbackSeller?.label;
  const descriptionHtml = sanitizeDescriptionHtml(normalizeDescriptionHtml(product.description));

  const sellerLinks = sellerContactUrl
    ? [
        {
          id: "product-seller",
          label: sellerContactLabel ?? "Написать продавцу",
          url: sellerContactUrl
        }
      ]
    : store.contactLinks
        .filter((link) => link.enabled && link.placement === "seller")
        .sort((a, b) => a.position - b.position)
        .map((link) => ({ id: link.id, label: link.label, url: link.url }));

  function openSeller(url: string) {
    window.open(url, "_blank", "noreferrer");
  }

  function moveLightbox(delta: number) {
    setLightboxIndex((current) => {
      if (current === null) return current;
      return (current + delta + product.media.length) % product.media.length;
    });
  }

  function handleLightboxTouchStart(event: TouchEvent<HTMLDivElement>) {
    const touch = event.touches[0];
    if (!touch) return;
    lightboxTouchStart.current = { x: touch.clientX, y: touch.clientY };
  }

  function handleLightboxTouchEnd(event: TouchEvent<HTMLDivElement>) {
    const start = lightboxTouchStart.current;
    lightboxTouchStart.current = null;
    const touch = event.changedTouches[0];
    if (!start || !touch || product.media.length < 2) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY) * 1.3) return;

    event.stopPropagation();
    moveLightbox(deltaX < 0 ? 1 : -1);
  }

  return (
    <article className="feed-item" data-slug={product.slug} ref={refCallback}>
      <div className="media-strip">
        {product.media.length > 0 ? (
          product.media.map((media, index) => (
            <button className="media-thumb" key={media.id} onClick={() => setLightboxIndex(index)} aria-label="Открыть фото">
              <img
                src={media.url}
                alt={media.altText ?? product.title}
                onError={(event) => {
                  event.currentTarget.src = "/placeholders/product.svg";
                }}
              />
            </button>
          ))
        ) : (
          <button className="media-thumb" onClick={() => undefined} aria-label="Фото отсутствует">
            <img src="/placeholders/product.svg" alt="" />
          </button>
        )}
      </div>
      <div className="detail-copy">
        <p className="status-pill">{statusLabel(product.availabilityStatus)}</p>
        <h2>{product.title}</h2>
        <div className="formatted-description" dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
        <div className="contact-row">
          {sellerLinks.map((link) => (
            <button className="primary-link" onClick={() => openSeller(link.url)} key={link.id}>
              {link.label}
            </button>
          ))}
        </div>
      </div>
      {lightboxIndex !== null && product.media[lightboxIndex] ? (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          onClick={() => setLightboxIndex(null)}
          onTouchStart={handleLightboxTouchStart}
          onTouchEnd={handleLightboxTouchEnd}
          onTouchCancel={() => {
            lightboxTouchStart.current = null;
          }}
        >
          <button className="lightbox-close" onClick={() => setLightboxIndex(null)} aria-label="Закрыть">
            ×
          </button>
          <img
            src={product.media[lightboxIndex].url}
            alt={product.media[lightboxIndex].altText ?? product.title}
            onClick={(event) => event.stopPropagation()}
            onError={(event) => {
              event.currentTarget.src = "/placeholders/product.svg";
            }}
          />
          <div className="lightbox-count">
            {lightboxIndex + 1} / {product.media.length}
          </div>
        </div>
      ) : null}
    </article>
  );
}

function AdminView({
  products,
  onBack,
  onProductsChange,
  isAdmin,
  adminChecked
}: {
  products: Product[];
  onBack: () => void;
  onProductsChange: (products: Product[]) => void;
  isAdmin: boolean;
  adminChecked: boolean;
}) {
  const [drafts, setDrafts] = useState(products);
  const [message, setMessage] = useState("");

  async function saveProduct(product: Product) {
    setMessage("Сохраняем...");
    try {
      const saved = await api.updateProduct(product.id, product);
      const nextProducts = drafts.map((item) => (item.id === saved.id ? saved : item));
      setDrafts(nextProducts);
      onProductsChange(nextProducts);
      setMessage("Сохранено");
    } catch {
      setMessage("Не удалось сохранить. Проверьте админ-сессию.");
    }
  }

  return (
    <section className="admin-panel">
      <div className="admin-heading">
        <button className="text-button" onClick={onBack}>
          Назад
        </button>
        <h2>Админка</h2>
        <span>{message}</span>
      </div>
      {!adminChecked ? <div className="locked-panel">Проверяем доступ...</div> : null}
      {adminChecked && !isAdmin ? (
        <div className="locked-panel">
          <h3>Вход администратора</h3>
          <p>
            Редактирование доступно только после Telegram Login. Backend уже принимает вход через
            <code> /api/admin/login</code> и выдает защищенную admin cookie для разрешенных Telegram ID.
          </p>
        </div>
      ) : null}
      {adminChecked && isAdmin
        ? drafts.map((product) => (
            <article className="admin-product" key={product.id}>
          <img
            src={product.media[0]?.url ?? "/placeholders/product.svg"}
            alt=""
            onError={(event) => {
              event.currentTarget.src = "/placeholders/product.svg";
            }}
          />
              <label>
                Название
                <input
                  value={product.title}
                  onChange={(event) =>
                    setDrafts((items) => items.map((item) => (item.id === product.id ? { ...item, title: event.target.value } : item)))
                  }
                />
              </label>
              <label>
                Описание
                <textarea
                  value={product.description}
                  onChange={(event) =>
                    setDrafts((items) =>
                      items.map((item) => (item.id === product.id ? { ...item, description: event.target.value } : item))
                    )
                  }
                />
              </label>
              <label>
                Цена
                <input
                  type="number"
                  value={product.price ?? ""}
                  onChange={(event) =>
                    setDrafts((items) =>
                      items.map((item) =>
                        item.id === product.id ? { ...item, price: Number(event.target.value) || undefined } : item
                      )
                    )
                  }
                />
              </label>
              <label>
                Видимость
                <select
                  value={product.visibility}
                  onChange={(event) =>
                    setDrafts((items) =>
                      items.map((item) =>
                        item.id === product.id ? { ...item, visibility: event.target.value as Product["visibility"] } : item
                      )
                    )
                  }
                >
                  <option value="published">Опубликован</option>
                  <option value="hidden">Скрыт</option>
                </select>
              </label>
              <label>
                Удалить после
                <input
                  type="datetime-local"
                  value={product.expiresAt.slice(0, 16)}
                  onChange={(event) =>
                    setDrafts((items) =>
                      items.map((item) =>
                        item.id === product.id ? { ...item, expiresAt: new Date(event.target.value).toISOString() } : item
                      )
                    )
                  }
                />
              </label>
              <button className="primary-link" onClick={() => saveProduct(product)}>
                Сохранить
              </button>
            </article>
          ))
        : null}
    </section>
  );
}

function statusLabel(status: Product["availabilityStatus"]) {
  const labels: Record<Product["availabilityStatus"], string> = {
    available: "В наличии",
    reserved: "Зарезервировано",
    sold: "Продано",
    hidden: "Скрыто"
  };
  return labels[status];
}

function extractSellerFromDescription(description: string) {
  const decoded = decodeHtmlBreaks(description);
  const match = decoded.match(/(?:^|[\s(])@([A-Za-z0-9_]{5,32})\b/);
  if (!match?.[1]) return null;
  return {
    username: match[1],
    label: `Написать @${match[1]}`,
    url: `https://t.me/${match[1]}`
  };
}

function normalizeDescriptionHtml(description: string) {
  return decodeHtmlBreaks(description)
    .replace(/^(?:<br>\s*)+|(?:\s*<br>)+$/g, "")
    .replace(/(?:<br>\s*){2,}/g, "<br>");
}

function decodeHtmlBreaks(value: string) {
  return value.replace(/&lt;br\s*\/?&gt;/gi, "<br>").replace(/<br\s*\/?>/gi, "<br>");
}

function sanitizeDescriptionHtml(html: string) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["a", "br", "strong", "em", "u", "s", "code", "pre"],
    ALLOWED_ATTR: ["href", "target", "rel"],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|tg):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i
  });
}
